import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Shell, PageHeader } from '../components/Shell.jsx';
import { Field, Input, Textarea } from '../components/Field.jsx';

const EMPTY = {
  name: '', email: '', role: '', outlet: '', outlet_url: '',
  beat: '', linkedin_url: '', twitter_handle: '', evidence: '', notes: '',
};

export default function EditProspect() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState({});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function load() {
    try {
      const r = await fetch(`/api/prospects/${id}`, { credentials: 'include' });
      if (!r.ok) { setErr({ form: `could not load (${r.status})` }); return; }
      const d = await r.json();
      const p = d.prospect;
      setForm({
        name: p.name || '',
        email: p.email || '',
        role: p.role || '',
        outlet: p.outlet || '',
        outlet_url: p.outlet_url || '',
        beat: p.beat || '',
        linkedin_url: p.linkedin_url || '',
        twitter_handle: p.twitter_handle || '',
        evidence: p.evidence || '',
        notes: p.notes || '',
      });
    } catch (e) {
      setErr({ form: String(e.message || e) });
    } finally {
      setLoading(false);
    }
  }

  function update(k) {
    return e => {
      setForm(f => ({ ...f, [k]: e.target.value }));
      setErr(({ [k]: _gone, ...rest }) => rest);
    };
  }

  function normalizeUrl(s) {
    if (!s) return '';
    const t = s.trim();
    if (!t) return '';
    if (/^https?:\/\//i.test(t)) return t;
    return 'https://' + t.replace(/^\/+/, '');
  }

  async function onSubmit(e) {
    e.preventDefault();
    const errors = {};
    if (!form.name.trim()) errors.name = 'name is required';
    if (!form.evidence.trim()) errors.evidence = 'evidence is required';
    if (Object.keys(errors).length) { setErr(errors); return; }

    setBusy(true);
    try {
      const payload = {
        ...form,
        outlet_url:   normalizeUrl(form.outlet_url),
        linkedin_url: normalizeUrl(form.linkedin_url),
        twitter_handle: form.twitter_handle.trim().replace(/^@/, ''),
      };
      const r = await fetch(`/api/prospects/${id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr({ form: d.message || 'save failed' });
        return;
      }
      nav(`/prospects/${id}`);
    } catch (e) {
      setErr({ form: String(e.message || e) });
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <Shell><div className="row muted"><span className="spinner" /> Loading prospect</div></Shell>;
  }

  return (
    <Shell narrow>
      <PageHeader
        eyebrow="Editing"
        title={form.name || 'Prospect'}
        sub="Update prospect details. Email verification status will not change."
        right={<Link to={`/prospects/${id}`} className="btn btn-ghost btn-sm">← Back</Link>}
      />

      <form onSubmit={onSubmit} className="stack-6">
        <div className="card card-pad">
          <h3 style={{ marginBottom: 'var(--space-4)' }}>Identity</h3>
          <div className="grid-2">
            <Field label="Name" required htmlFor="name" error={err.name}>
              <Input id="name" value={form.name} onChange={update('name')} autoFocus />
            </Field>
            <Field label="Role / Title" htmlFor="role">
              <Input id="role" value={form.role} onChange={update('role')} />
            </Field>
          </div>
          <div className="grid-2" style={{ marginTop: 'var(--space-4)' }}>
            <Field label="Email" htmlFor="email">
              <Input id="email" type="email" value={form.email} onChange={update('email')} />
            </Field>
            <Field label="Outlet" htmlFor="outlet">
              <Input id="outlet" value={form.outlet} onChange={update('outlet')} />
            </Field>
          </div>
        </div>

        <div className="card card-pad">
          <h3 style={{ marginBottom: 'var(--space-4)' }}>Beat &amp; coverage</h3>
          <div className="grid-2">
            <Field label="Beat" htmlFor="beat">
              <Input id="beat" value={form.beat} onChange={update('beat')} />
            </Field>
            <Field label="Outlet URL" htmlFor="outlet_url" help="www.example.com is fine — I'll add https:// for you.">
              <Input id="outlet_url" value={form.outlet_url} onChange={update('outlet_url')} />
            </Field>
          </div>
          <div className="grid-2" style={{ marginTop: 'var(--space-4)' }}>
            <Field label="LinkedIn URL" htmlFor="linkedin_url">
              <Input id="linkedin_url" value={form.linkedin_url} onChange={update('linkedin_url')} />
            </Field>
            <Field label="Twitter/X handle" htmlFor="twitter_handle">
              <Input id="twitter_handle" value={form.twitter_handle} onChange={update('twitter_handle')} />
            </Field>
          </div>
        </div>

        <div className="card card-pad">
          <h3 style={{ marginBottom: 'var(--space-3)' }}>Evidence</h3>
          <Field label="Why are they a prospect" required htmlFor="evidence" error={err.evidence}
            help="The specific connection between this person and your story.">
            <Textarea id="evidence" value={form.evidence} onChange={update('evidence')} />
          </Field>
        </div>

        <div className="card card-pad">
          <Field label="Notes" htmlFor="notes" help="Internal only.">
            <Textarea id="notes" value={form.notes} onChange={update('notes')} />
          </Field>
        </div>

        {err.form ? (
          <div className="card card-pad" style={{ borderColor: 'var(--danger)' }}>
            <div style={{ color: 'var(--danger)' }}>{err.form}</div>
          </div>
        ) : null}

        <div className="row" style={{ gap: 12 }}>
          <button type="submit" className="btn btn-lg" disabled={busy}>
            {busy ? <><span className="spinner" /> Saving</> : 'Save changes'}
          </button>
          <Link to={`/prospects/${id}`} className="btn btn-ghost">Cancel</Link>
        </div>
      </form>
    </Shell>
  );
}
